/**
 * What a publish changes in the solution's ACTION CODE — the scripts on the
 * Actions page (`services/…` functions and the top-level `.py` config files),
 * which live as version files, not in the node table `solution-diff.ts`
 * compares. An edited action changes what the solution does as surely as an
 * edited node, so Deploy shows it beside them.
 */

/** A file the Actions page manages: a `services/` script or a top-level `.py`. */
export const isActionFile = (path: string): boolean =>
  (path.startsWith('services/') && !path.endsWith('/')) ||
  /^[^/]+\.py$/.test(path);

export type DiffLineKind = 'same' | 'add' | 'rm';

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

export interface ActionFileChange {
  path: string;
  /** The file's name, without its folder. */
  name: string;
  change: 'added' | 'edited' | 'removed';
  added: number;
  removed: number;
  /** The changed lines with a little context; empty for a file too large to diff. */
  lines: DiffLine[];
  /** Contents withheld: the file holds a private key. `lines` is empty. */
  secret?: boolean;
}

/** Past this many lines a side, a file is reported by count, not line by line. */
const MAX_DIFF_LINES = 2_000;
const CONTEXT = 2;

const splitLines = (s: string) => s.replace(/\r\n/g, '\n').split('\n');

/** Line diff by longest common subsequence — action files are small. */
export const diffLines = (before: string, after: string): DiffLine[] => {
  const a = splitLines(before);
  const b = splitLines(after);
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: 'rm', text: a[i] });
      i += 1;
    } else {
      out.push({ kind: 'add', text: b[j] });
      j += 1;
    }
  }
  while (i < n) out.push({ kind: 'rm', text: a[(i += 1) - 1] });
  while (j < m) out.push({ kind: 'add', text: b[(j += 1) - 1] });
  return out;
};

/** Only the changed lines and `CONTEXT` lines around each, with gaps as "…". */
export const withContext = (lines: DiffLine[]): DiffLine[] => {
  const keep = lines.map(() => false);
  lines.forEach((l, i) => {
    if (l.kind === 'same') return;
    for (
      let k = Math.max(0, i - CONTEXT);
      k <= Math.min(lines.length - 1, i + CONTEXT);
      k += 1
    ) {
      keep[k] = true;
    }
  });
  const out: DiffLine[] = [];
  let folded = false;
  lines.forEach((l, i) => {
    if (keep[i]) {
      out.push(l);
      folded = false;
    } else if (!folded) {
      // One "…" per run of unchanged lines left out.
      out.push({ kind: 'same', text: '…' });
      folded = true;
    }
  });
  return out;
};

const PEM_PRIVATE_KEY_BEGIN =
  /^[ \t]*-----BEGIN ((?:[A-Z0-9]+ )*)PRIVATE KEY( BLOCK)?-----[ \t]*$/;
const BASE64_BODY_LINE = /^[ \t]*[A-Za-z0-9+/=]+[ \t]*$/;
const PEM_HEADER_LINE = /^[ \t]*[A-Za-z0-9-]+:/;
const PEM_LINE_BREAK = /\r?\n/;

/**
 * A whole PEM/PGP private-key block: the BEGIN line, a body, the matching END
 * line. Each line between them must be base64 (or a `Name: value` armor/PEM
 * header line, or blank), and at least one must be base64 — so a header string
 * in a parser (`startswith("-----BEGIN PRIVATE KEY-----")`) or a block with code
 * between the markers is not a match. The END line must repeat the key type
 * (`RSA `, `EC `, `PGP `, empty) and PGP's ` BLOCK` of the BEGIN line.
 *
 * Parsed line by line, not with one regex over the body: the body pattern that
 * did this (an optional line group repeated lazily) backtracked exponentially on
 * a BEGIN with no END followed by blank or header lines — measured 640 ms for 13
 * blank indented lines and 2216 ms for 13 header lines with trailing spaces, on
 * a file the Actions editor and the publish diff read as soon as it is opened.
 * Here every line is tested by a regex that is anchored and has no adjacent
 * quantifiers over overlapping classes, and a line is visited once as a body
 * line of one BEGIN and once as a candidate BEGIN, so the work is linear in the
 * number of lines (pinned by the adversarial cases in the tests, not proven).
 */
const isPemPrivateKeyBlock = (text: string): boolean => {
  // Every BEGIN line `PEM_PRIVATE_KEY_BEGIN` accepts contains this literal, so
  // a text without it skips the split below.
  if (!text.includes('PRIVATE KEY')) return false;
  const lines = text.split(PEM_LINE_BREAK);
  let i = 0;
  while (i < lines.length) {
    const begin = PEM_PRIVATE_KEY_BEGIN.exec(lines[i]);
    if (begin === null) {
      i += 1;
      continue;
    }
    const end = `-----END ${begin[1]}PRIVATE KEY${begin[2] ?? ''}-----`;
    let sawBase64 = false;
    let j = i + 1;
    for (; j < lines.length; j += 1) {
      const line = lines[j];
      if (line.trimStart().startsWith(end)) {
        if (sawBase64) return true;
        break;
      }
      if (BASE64_BODY_LINE.test(line)) sawBase64 = true;
      else if (line.trim() !== '' && !PEM_HEADER_LINE.test(line)) break;
    }
    // This BEGIN failed at line `j`; line `j` may itself be a BEGIN, so the
    // scan resumes there. `j > i`, so the scan always moves forward.
    i = Math.max(j, i + 1);
  }
  return false;
};

const PEM_PRIVATE_KEY_HEADER = /^\s*-----BEGIN (?:[A-Z0-9]+ )*PRIVATE KEY-----/;

/** A service-account file: JSON whose `private_key` is a string starting with a PEM private-key header. */
const isServiceAccountJson = (text: string): boolean => {
  if (!text.includes('private_key')) return false;
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) return false;
    const key = (parsed as Record<string, unknown>).private_key;
    return typeof key === 'string' && PEM_PRIVATE_KEY_HEADER.test(key);
  } catch {
    return false;
  }
};

/**
 * True when the text is real private-key material, recognised in two shapes:
 * (1) a whole PEM private-key block (`-----BEGIN [type ]PRIVATE KEY-----`, a
 * non-empty base64 body, the matching END line), PGP's
 * `PRIVATE KEY BLOCK` form included; (2) a JSON document whose top-level
 * `private_key` is a string starting with a PEM private-key header (a service
 * account file). It decides by CONTENT because an uploaded key keeps the
 * customer's original file name, whatever its extension (see
 * `pages/talk/knowledge-keys.ts`).
 *
 * Decides what the Actions editor may show and what the publish diff may print,
 * so a false positive hides an ordinary script and a false negative prints a key.
 * NOT detected (false negatives): a service-account file whose `private_key` was
 * rotated to a non-PEM value or is nested below the top level; a PEM block
 * with an unusual body line (anything outside base64 and `Name: value` header
 * lines); a key pasted into a script as an escaped one-line string; other secret
 * shapes (an API token inside a .py, a password in a config). NOT flagged (by
 * design, false positives removed): a script that merely names `private_key`
 * or holds a PEM header/footer string.
 */
export const looksLikeCredential = (text: string): boolean =>
  isPemPrivateKeyBlock(text) || isServiceAccountJson(text);

const nameOf = (path: string) => path.split('/').pop() || path;

/**
 * The action files that differ between two versions, by path. `before` is
 * null on a first publish (everything is new).
 */
export const diffActionFiles = (
  before: Record<string, string> | null,
  after: Record<string, string>
): ActionFileChange[] => {
  const prev = before ?? {};
  const paths = [...new Set([...Object.keys(prev), ...Object.keys(after)])]
    .filter(isActionFile)
    .sort();
  return paths.flatMap((path): ActionFileChange[] => {
    const was = prev[path];
    const now = after[path];
    if (was === now) return [];
    const change =
      was === undefined ? 'added' : now === undefined ? 'removed' : 'edited';
    const a = was ?? '';
    const b = now ?? '';
    if (looksLikeCredential(a) || looksLikeCredential(b)) {
      // Never run a diff over a key: list the change, withhold the contents.
      return [
        {
          path,
          name: nameOf(path),
          change,
          added: change === 'removed' ? 0 : splitLines(b).length,
          removed: change === 'added' ? 0 : splitLines(a).length,
          lines: [],
          secret: true,
        },
      ];
    }
    const tooBig =
      splitLines(a).length > MAX_DIFF_LINES ||
      splitLines(b).length > MAX_DIFF_LINES;
    const all = tooBig ? [] : diffLines(a, b);
    const count = (kind: DiffLineKind) =>
      tooBig
        ? splitLines(kind === 'add' ? b : a).length
        : all.filter((l) => l.kind === kind).length;
    return [
      {
        path,
        name: nameOf(path),
        change,
        added: change === 'removed' ? 0 : count('add'),
        removed: change === 'added' ? 0 : count('rm'),
        lines: tooBig ? [] : withContext(all),
      },
    ];
  });
};

/** One line per changed file, for the AI-drafted version notes. */
export const describeActionChanges = (changes: ActionFileChange[]): string =>
  changes
    .map(
      (c) => `Action code ${c.change}: ${c.path} (+${c.added} −${c.removed})`
    )
    .join('\n');
